import { Monitor, Smartphone, Sparkles } from "lucide-react";
import { Button, DeviceFrame } from "@/ui";
import type { NaraBlock } from "@/mock";
import { cn } from "@/lib/cn";
import { PreviewBlock } from "./PreviewBlocks";
import type { MakeMode } from "./makeModes";

type DeviceView = "desktop" | "mobile";

/**
 * The live preview canvas — a warm, on-brand mock landing page composed from
 * the streamed blocks. Renders desktop (browser chrome) or mobile (device
 * frame). In A/B mode the canvas shows the active variant being previewed.
 */
export function PreviewCanvas({
  blocks,
  streaming,
  mode,
  device,
  onDeviceChange,
  variantLabel,
  domain,
}: {
  blocks: NaraBlock[];
  streaming: boolean;
  mode: MakeMode;
  device: DeviceView;
  onDeviceChange: (d: DeviceView) => void;
  variantLabel?: string;
  domain: string;
}) {
  const empty = blocks.length === 0 && !streaming;

  const page = (
    <div className="bg-canvas">
      {blocks.map((b) => (
        <PreviewBlock key={b.id} block={b} />
      ))}
      {streaming && (
        <div className="flex items-center justify-center gap-2 px-6 py-8 text-[12px] text-muted">
          <Sparkles className="h-4 w-4 animate-pulse text-accent" strokeWidth={1.5} />
          Nara is composing…
        </div>
      )}
    </div>
  );

  return (
    <div className="rounded-card border border-hairline bg-paper-2 p-3 dark:bg-white/5">
      {/* canvas toolbar */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="eyebrow">Live preview</span>
          {mode === "ab" && variantLabel && (
            <span className="rounded-chip bg-paper px-2 py-0.5 font-mono text-[10px] text-muted shadow-sm dark:bg-white/10 dark:shadow-none">
              {variantLabel}
            </span>
          )}
        </div>
        <div className="inline-flex rounded-full border border-hairline bg-paper p-0.5 dark:bg-white/5">
          {([
            { v: "desktop", Icon: Monitor },
            { v: "mobile", Icon: Smartphone },
          ] as const).map(({ v, Icon }) => (
            <button
              key={v}
              onClick={() => onDeviceChange(v)}
              aria-label={`Preview ${v}`}
              className={cn(
                "grid h-7 w-8 place-items-center rounded-full transition-colors",
                device === v ? "bg-paper-2 text-ink-strong dark:bg-white/10" : "text-muted hover:text-ink",
              )}
            >
              <Icon className="h-[15px] w-[15px]" strokeWidth={1.5} />
            </button>
          ))}
        </div>
      </div>

      {empty ? (
        <EmptyCanvas />
      ) : device === "mobile" ? (
        <div className="py-2">
          <DeviceFrame>
            <div className="h-full overflow-y-auto pt-5">{page}</div>
          </DeviceFrame>
        </div>
      ) : (
        <div className="overflow-hidden rounded-card border border-hairline bg-canvas shadow-sm dark:shadow-none">
          {/* browser chrome */}
          <div className="flex items-center gap-2 border-b border-hairline bg-paper px-3 py-2">
            <span className="flex gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-hairline" />
              <span className="h-2.5 w-2.5 rounded-full bg-hairline" />
              <span className="h-2.5 w-2.5 rounded-full bg-hairline" />
            </span>
            <div className="ml-1 flex-1 truncate rounded-chip bg-paper-2 px-2.5 py-1 text-center font-mono text-[10px] text-subtle dark:bg-white/5">
              {domain}
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">{page}</div>
        </div>
      )}
    </div>
  );
}

function EmptyCanvas() {
  return (
    <div className="grid place-items-center rounded-card border border-dashed border-hairline bg-canvas px-6 py-16 text-center">
      <Sparkles className="h-7 w-7 text-subtle" strokeWidth={1.25} />
      <p className="mt-3 text-[14px] font-medium text-ink-strong">Your page appears here</p>
      <p className="mt-1 max-w-[34ch] text-[13px] leading-relaxed text-muted">
        Tell Nara what to make and she'll draft it block by block — you approve, then publish.
      </p>
      <div className="mt-4">
        <Button variant="ghost" size="sm" disabled>
          Nothing to preview yet
        </Button>
      </div>
    </div>
  );
}
